/**
 * Деньги на фронте.
 *
 * Правило: источник истины — сервер (Decimal / NUMERIC(18,2)). Здесь только
 * форматирование и превью-арифметика для UI. Ни одной операции во float:
 * все вычисления идут через BigInt центов, поэтому "0.1" + "0.2" даст "0.30",
 * а не 0.30000000000000004.
 */

import i18n from '@/shared/i18n';

const SCALE = 2n;
const SCALE_FACTOR = 100n;

export interface FormatMoneyOptions {
  /** Показывать знак «+» у положительных величин (начисления). */
  signed?: boolean;
  /** Число знаков после запятой. По умолчанию 2. */
  fractionDigits?: number;
  /** Отдать только число, без символа/кода валюты. */
  bare?: boolean;
  /** Локаль-оверрайд; по умолчанию берётся текущая из i18next. */
  locale?: string;
}

/** Текущая BCP-47 локаль из i18next; вне инициализации — 'ru'. */
export function currentLocale(): string {
  const raw = i18n.language ?? i18n.options?.fallbackLng;
  if (typeof raw === 'string' && raw.length > 0) return raw;
  return 'ru';
}

/* -------------------------------------------------------------------------- */
/* Парсинг                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Нормализует пользовательский ввод в каноническую строку "123.45".
 * Принимает запятую как десятичный разделитель, пробелы/неразрывные пробелы
 * как групповые. Возвращает null, если строка не является числом.
 */
export function parseMoneyInput(input: string): string | null {
  if (typeof input !== 'string') return null;
  const cleaned = input
    // Разделители групп: пробел, NBSP (\u00A0), узкий NBSP (\u202F), тонкий
    // пробел (\u2009) и апостроф — швейцарская запись. Заданы escape-
    // последовательностями: литеральные невидимые символы в исходнике
    // неотличимы друг от друга и ловятся линтером как irregular whitespace.
    .replace(/[\s\u00A0\u202F\u2009']/g, '')
    .replace(/,/g, '.')
    .trim();
  if (cleaned === '' || cleaned === '.' || cleaned === '-' || cleaned === '-.') return null;
  if (!/^-?\d*\.?\d*$/.test(cleaned)) return null;

  const negative = cleaned.startsWith('-');
  const unsigned = negative ? cleaned.slice(1) : cleaned;
  const [intRaw = '', fracRaw = ''] = unsigned.split('.');
  if (intRaw === '' && fracRaw === '') return null;

  const int = intRaw.replace(/^0+(?=\d)/, '') || '0';
  const frac = fracRaw.slice(0, 2).padEnd(2, '0');
  const normalized = `${int}.${frac}`;
  if (normalized === '0.00') return '0.00';
  return negative ? `-${normalized}` : normalized;
}

/** Строка -> BigInt копеек. Некорректный ввод -> 0n. */
export function toCents(value: string | null | undefined): bigint {
  if (value == null) return 0n;
  const normalized = parseMoneyInput(value);
  if (normalized === null) return 0n;
  const negative = normalized.startsWith('-');
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [int = '0', frac = '00'] = unsigned.split('.');
  const cents = BigInt(int) * SCALE_FACTOR + BigInt(frac);
  return negative ? -cents : cents;
}

/**
 * Денежная строка -> number для визуализации (графики, доли, прогресс).
 *
 * Единственное законное место превращения суммы в число с плавающей точкой:
 * счёт идёт через копейки, поэтому "0.10" + "0.20" не даёт 0.30000000000000004.
 * Для арифметики и вывода используйте addMoney/subMoney/formatMoney — number
 * годится только там, где значение всё равно уходит в пиксели.
 */
export function toNumber(value: string | null | undefined): number {
  return Number(toCents(value)) / 100;
}

/** BigInt копеек -> каноническая строка "123.45". */
export function fromCents(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const int = abs / SCALE_FACTOR;
  const frac = abs % SCALE_FACTOR;
  const body = `${int}.${frac.toString().padStart(Number(SCALE), '0')}`;
  return negative ? `-${body}` : body;
}

/* -------------------------------------------------------------------------- */
/* Арифметика (только для превью в UI)                                         */
/* -------------------------------------------------------------------------- */

export function addMoney(a: string, b: string): string {
  return fromCents(toCents(a) + toCents(b));
}

export function subMoney(a: string, b: string): string {
  return fromCents(toCents(a) - toCents(b));
}

/** -1 | 0 | 1 */
export function cmpMoney(a: string, b: string): -1 | 0 | 1 {
  const left = toCents(a);
  const right = toCents(b);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

export function isZeroMoney(value: string): boolean {
  return toCents(value) === 0n;
}

export function isNegativeMoney(value: string): boolean {
  return toCents(value) < 0n;
}

export function maxMoney(a: string, b: string): string {
  return cmpMoney(a, b) >= 0 ? a : b;
}

export function clampMoney(value: string, min: string, max: string): string {
  if (cmpMoney(value, min) < 0) return min;
  if (cmpMoney(value, max) > 0) return max;
  return value;
}

/* -------------------------------------------------------------------------- */
/* Форматирование                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Intl.NumberFormat дорогой в конструировании — на списках из сотни строк это
 * заметно даже на десктопе, а в Telegram WebView тем более. Кэшируем.
 */
const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const created = new Intl.NumberFormat(locale, options);
  formatterCache.set(key, created);
  return created;
}

/** Валютные коды, которые Intl отрендерит корректно как currency. */
const ISO_CURRENCY = /^[A-Z]{3}$/;

/**
 * Форматирует денежную строку. USDT не является ISO-валютой, поэтому его
 * дописываем суффиксом вручную, а не через `style: 'currency'`.
 */
export function formatMoney(
  value: string | null | undefined,
  currency?: string,
  opts: FormatMoneyOptions = {},
): string {
  const { signed = false, fractionDigits = 2, bare = false, locale = currentLocale() } = opts;
  const cents = toCents(value ?? '0');
  const numeric = Number(cents) / 100;

  const isIso = currency !== undefined && ISO_CURRENCY.test(currency) && currency !== 'USDT';
  const formatted = getFormatter(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: true,
    ...(isIso ? { style: 'currency' as const, currency } : {}),
    ...(signed ? { signDisplay: 'exceptZero' as const } : {}),
  }).format(numeric);

  if (bare || currency === undefined || isIso) return formatted;
  return `${formatted} ${currency}`;
}

const COMPACT_STEPS: { limit: bigint; div: bigint; suffix: 'K' | 'M' | 'B' }[] = [
  { limit: 100_000_000_000n, div: 100_000_000_000n, suffix: 'B' },
  { limit: 100_000_000n, div: 100_000_000n, suffix: 'M' },
  { limit: 1_000_000n, div: 100_000n, suffix: 'K' },
];

/**
 * Компактная форма для крупных чисел: 1 234 567.00 -> "1.23M".
 * Порог — 10 000: ниже него точная сумма читается лучше сокращения.
 */
export function formatCompact(
  value: string | null | undefined,
  currency?: string,
  opts: Pick<FormatMoneyOptions, 'locale'> = {},
): string {
  const locale = opts.locale ?? currentLocale();
  const cents = toCents(value ?? '0');
  const abs = cents < 0n ? -cents : cents;

  if (abs < 1_000_000n) {
    return formatMoney(value, currency, { locale, fractionDigits: abs % 100n === 0n ? 0 : 2 });
  }

  for (const step of COMPACT_STEPS) {
    if (abs >= step.limit) {
      const scaled = Number(cents) / Number(step.div);
      const body = getFormatter(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(scaled);
      const withSuffix = `${body}${step.suffix}`;
      return currency ? `${withSuffix} ${currency}` : withSuffix;
    }
  }

  return formatMoney(value, currency, { locale });
}

/** Процент из доли 0..1. `fullness` приходит числом, не строкой. */
export function formatPercent(ratio: number | null | undefined, fractionDigits = 0): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return getFormatter(currentLocale(), {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(ratio);
}

/** Целое число с группировкой (участники, кольца). */
export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return getFormatter(currentLocale(), { maximumFractionDigits: 0 }).format(value);
}
